import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { Job, JSONApiDataObjectLoadable, OuterJob, ReduxEntity } from 'Douleutaras/types/index';
import { OuterJobFormValues } from 'views/job/post-external-job/components/PostExternalJobForm';
import { RootState } from 'Douleutaras/redux/store';
import normalize from 'json-api-normalizer';

const outerJobsAdapter = createEntityAdapter<JSONApiDataObjectLoadable<OuterJob>>({
  selectId: job => job.id
});

const API_OUTER_JOB_ENDPOINT = 'api/public/v3/outer-job';

export const patchOuterJob = createAsyncThunk(
  'outerJobs/patch',
  async ({ id, attributes }: { id: string, attributes: Partial<OuterJobFormValues> }, api) => {
    const { changeExternalBidLoadingState: updateLoadingState } = outerJobSlice.actions;

    await api.dispatch(updateLoadingState({ id, loading: true }));

    try {
      const response = await makeRequest(
        `${API_OUTER_JOB_ENDPOINT}/${id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: 'outer-job',
              id,
              attributes
            }
          }
        }
      )(api);

      await api.dispatch(updateLoadingState({ id, loading: false }));

      const ok = response.status === 200 || response.status === 201;

      if (!ok) {
        if (response.payload?.response?.data.errors) {
          return api.rejectWithValue({errors: response.payload.response.data.errors});
        }

        return api.rejectWithValue({serverError: `${response.payload?.response?.status}`});
      }

      return normalize({ data: [ response.data.data ] });

    } catch (error) {
      await api.dispatch(updateLoadingState({ id, loading: false }));

      return api.rejectWithValue({ error: error.message });
    }
  }
);

export const postOuterJob = createAsyncThunk(
  'outerJobs/post',
  async (values: OuterJobFormValues, api) => {

    const attributes = {
      ...values,
      bnEntry: { // Needed to validate API
        any: 'garbage'
      }
    };

    const response = await makeRequest(
      `${API_OUTER_JOB_ENDPOINT}/`,
      {
        method: 'POST',
        body: {
          data: {
            type: 'outer-job',
            attributes
          }
        }
      }
    )(api);

    const ok = response.status === 200 || response.status === 201;

    if (!ok) {
      return {
        error: true,
        errors: response.payload?.response?.data.errors,
        responseStatus: response.payload?.response?.status
      };
    }

    return response.data;

  });

export const getOuterJob = createAsyncThunk(
  'outerJob/get',
  async ({ id }: { id: string }, api) => {
    const response = await makeRequest(
      `${API_OUTER_JOB_ENDPOINT}/${id}`
    )(api);

    const ok = response.status === 200 || response.status === 201;
    if (!ok) {
      throw new Error(response.status);
    }

    return normalize({ data: [ response.data.data ] });
  });

export const outerJobSlice = createSlice({
  name: 'outerJobs',
  initialState: outerJobsAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObjectLoadable<Job>>),
  reducers: {
    outerJobsAdded: (state, action) => {
      outerJobsAdapter.setMany(state, action.payload);
    },
    changeExternalBidLoadingState: (state, action) => {
      state.entities[action.payload.id].loading = action.payload.loading;
    }
  },
  extraReducers: builder => {
    builder.addCase(patchOuterJob.fulfilled, (state, action) => {
      outerJobsAdapter.setMany(state, action.payload.outerJob);
      state.loading = false;
      state.error = false;
    });
    builder.addCase(patchOuterJob.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(patchOuterJob.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getOuterJob.fulfilled, (state, action) => {
      outerJobsAdapter.setMany(state, action.payload.outerJob);
      state.loading = false;
      state.error = false;
    });
    builder.addCase(getOuterJob.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(getOuterJob.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
  }
});

export const { changeExternalBidLoadingState } = outerJobSlice.actions;
export const outerJobSelectors = outerJobsAdapter.getSelectors((state: RootState) => state.outerJobs);
